import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useSubjects } from '../hooks/useSubjects';

export default function TeacherSettingsPage() {
    const ctx = useOutletContext();
    const { subjects: subjectsList } = useSubjects();
    const [profile, setProfile] = useState(null);
    const [school, setSchool] = useState('');
    const [subjects, setSubjects] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/api/teacher/me').then((r) => {
            setProfile(r.data);
            setSchool(r.data.school || '');
            setSubjects(r.data.subjects || []);
        }).catch(() => setProfile(false));
    }, []);

    const toggleSubject = (s) => {
        setSubjects((prev) => prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put('/api/teacher/profile', { school, subjects });
            toast.success('Profile saved');
            ctx?.refreshProfile?.();
        } catch { toast.error('Could not save'); }
        finally { setSaving(false); }
    };

    if (profile === null) return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 text-[#7C3AED] animate-spin" /></div>;
    if (profile === false) return <div className="text-sm text-zinc-500">Could not load profile.</div>;

    return (
        <div className="space-y-6 max-w-2xl" data-testid="teacher-settings-page">
            <div>
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Settings</h1>
                <p className="text-sm text-zinc-500 mt-1">Your teacher profile and join code.</p>
            </div>

            <div className="bg-white rounded-3xl border border-zinc-200 p-6 space-y-5">
                <Field label="Name">
                    <div className="text-sm font-bold">{profile.name}</div>
                </Field>
                <Field label="Email">
                    <div className="text-sm text-zinc-700">{profile.email}</div>
                </Field>
                <Field label="Teacher code">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#7C3AED] text-white font-black tracking-wide text-sm">{profile.teacher_code}</div>
                    <p className="text-xs text-zinc-500 mt-1">Share this + a class code with students so they can join.</p>
                </Field>
                <Field label="School">
                    <input value={school} onChange={(e) => setSchool(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-zinc-200 outline-none focus:border-[#7C3AED] text-sm" data-testid="settings-school" placeholder="e.g. Westminster Sixth Form" />
                </Field>
                <Field label="Subjects taught (select all that apply)">
                    <div className="flex flex-wrap gap-2">
                        {subjectsList.map((s) => (
                            <button key={s.id} onClick={() => toggleSubject(s.name)} className={`px-3.5 py-2 rounded-xl text-xs font-bold border-2 transition-colors ${subjects.includes(s.name) ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white text-zinc-600 border-zinc-200 hover:border-[#7C3AED]'}`} data-testid={`subject-${s.id}`}>
                                {s.name}
                            </button>
                        ))}
                    </div>
                </Field>
                <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-[#7C3AED] text-white font-bold text-sm px-5 py-2.5 rounded-xl hover:bg-[#6D28D9] disabled:opacity-60" data-testid="save-settings">
                    <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save changes'}
                </button>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <div className="text-xs font-bold text-zinc-600 mb-1.5">{label}</div>
            {children}
        </div>
    );
}
